import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SearchInput } from "@/components/ui/search-input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { FilterConfig } from "./types";

type DataTableToolbarProps = {
	search?: string;
	onSearchChange?: (value: string) => void;
	searchPlaceholder?: string;
	filters?: FilterConfig[];
	filterValues?: Record<string, string>;
	onFilterChange?: (key: string, value: string) => void;
	headerActions?: ReactNode;
};

export function DataTableToolbar({
	search,
	onSearchChange,
	searchPlaceholder,
	filters,
	filterValues,
	onFilterChange,
	headerActions,
}: DataTableToolbarProps) {
	const { t } = useTranslation();
	const hasContent =
		onSearchChange !== undefined ||
		(filters && filters.length > 0) ||
		headerActions !== undefined;

	if (!hasContent) return null;

	return (
		<div className="flex flex-wrap items-center gap-2">
			{onSearchChange !== undefined && (
				<SearchInput
					wrapperClassName="w-full sm:w-auto"
					className="w-full sm:w-72 md:w-96"
					placeholder={searchPlaceholder ?? t("dataTable.searchPlaceholder")}
					value={search ?? ""}
					onValueChange={onSearchChange}
				/>
			)}
			{filters?.map((filter) => (
				<Select
					key={filter.key}
					value={filterValues?.[filter.key] ?? ""}
					onValueChange={(value) =>
						onFilterChange?.(filter.key, value === "__all__" ? "" : value)
					}
				>
					<SelectTrigger className="w-40">
						<SelectValue placeholder={filter.label} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="__all__">
							{filter.allLabel ?? t("dataTable.all")}
						</SelectItem>
						{filter.options.map((opt) => (
							<SelectItem key={opt.value} value={opt.value}>
								{opt.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			))}
			{headerActions && <div className="ml-auto">{headerActions}</div>}
		</div>
	);
}
